import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import usePushSubscription from "@/hooks/usePushSubscription";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import EmailConfirmationBanner from "@/components/app/EmailConfirmationBanner";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FeedPage from "@/pages/FeedPage";
import ProfilePage from "@/pages/ProfilePage";
import BookPage from "@/pages/BookPage";
import SearchPage from "@/pages/SearchPage";
import SettingsPage from "@/pages/SettingsPage";
import NotificationsPage from "@/pages/NotificationsPage";

// Redirect authenticated users away from auth pages
function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/feed" replace />;
  return children;
}

function AppInner() {
  usePushSubscription();
  return (
    <>
      <EmailConfirmationBanner />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        }
      />
      <Route
        path="/register"
        element={
          <AuthRedirect>
            <RegisterPage />
          </AuthRedirect>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Semi-public (accessible to all, content varies by auth) */}
      <Route path="/search" element={<SearchPage />} />
      <Route path="/profile/:username" element={<ProfilePage />} />
      <Route path="/books/:bookId" element={<BookPage />} />
      <Route path="/books/google/:googleBooksId" element={<BookPage />} />

      {/* Protected */}
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <FeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
    <div className="paper-grain">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationsProvider>
          <AppInner />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#FDFBF7',
                border: '1px solid #F2EBE3',
                color: '#1C1917',
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
    </Sentry.ErrorBoundary>
  );
}

function AppCrashFallback() {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <img src="/logo.png" alt="ReadTheRoom" className="h-14 w-14 rounded-xl mx-auto mb-6" />
        <h1 className="font-heading text-2xl text-ink mb-2">Something went wrong</h1>
        <p className="text-ink-muted text-sm mb-6">
          We've been notified and are looking into it. Try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-terra hover:bg-terra/90 text-cream font-semibold px-6 py-2 rounded-lg text-sm"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

export default App;
